import { useState, useEffect, useContext } from "react";
import IncidenciasService from "../api/incidencias.api";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";
import { ESTADOS_NO_CERRADOS, ESTADOS } from "../constants";
import {
  transformarIncidenciasParaGrid,
  generarTextoModalIncidencia,
} from "../helpers/transformers";

/**
 * Hook para gestión de incidencias
 * @returns {Object} Estado y funciones para gestionar incidencias
 */
export const useIncidencias = () => {
  const { state, updateState, refrescarContadorIncidencias } =
    useContext(StateContext);

  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soloAbiertas, setSoloAbiertas] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [incidenciaSeleccionada, setIncidenciaSeleccionada] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);

  /**
   * Cargar incidencias del API con los filtros aplicados
   */
  const cargarIncidencias = async (params = {}) => {
    setLoading(true);
    try {
      const data = await IncidenciasService.list(params);
      const incidenciasTransformadas = transformarIncidenciasParaGrid(data);
      setIncidencias(incidenciasTransformadas);
    } catch (error) {
      const toast = Toasts.generateToast(
        error.message || "Error al cargar incidencias",
        "error"
      );
      updateState({ toasts: [...state.toasts, toast] });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Carga inicial: solo incidencias no cerradas del ejercicio actual
   */
  useEffect(() => {
    cargarIncidencias({
      estado: ESTADOS_NO_CERRADOS,
      ejercicio: state.year,
    });
  }, []);

  /**
   * Alternar entre ver solo abiertas y ver todas
   */
  const toggleFiltro = async () => {
    if (soloAbiertas) {
      // Ver todas: traer todas las incidencias del ejercicio
      setSoloAbiertas(false);
      await cargarIncidencias({ ejercicio: state.year });
    } else {
      // Ver solo abiertas: traer solo no cerradas
      setSoloAbiertas(true);
      await cargarIncidencias({
        estado: ESTADOS_NO_CERRADOS,
        ejercicio: state.year,
      });
    }
  };

  /**
   * Abrir modal de edición de incidencia
   */
  const handleEditarIncidencia = (id) => {
    const incidencia = incidencias.find((row) => row.id === id);
    if (!incidencia) return;

    // Generar texto para el modal
    const textoModal = generarTextoModalIncidencia(incidencia);

    setIncidenciaSeleccionada({
      ...incidencia,
      textoModal,
    });
    setShowModal(true);
  };

  /**
   * Cerrar modal
   */
  const handleCerrarModal = () => {
    setShowModal(false);
    setIncidenciaSeleccionada(null);
  };

  /**
   * Confirmar cambios en incidencia (actualizar + crear comentario)
   */
  const handleConfirmarCambios = async (datosActualizados) => {
    setLoadingModal(true);

    try {
      // 1. Actualizar incidencia primero (el API solo persiste estado).
      // Va antes que el comentario porque repetirla es inocuo: si fallara después
      // de crear el comentario, el reintento lo duplicaría y reenviaría el correo.
      const updateData = {
        note: datosActualizados.note,
        estado: datosActualizados.estado,
      };

      await IncidenciasService.update(datosActualizados.id_note, updateData);

      // 2. Crear comentario (y enviarlo por correo al cliente si se pidió)
      const comentarioData = {
        idNote: datosActualizados.id_note,
        comentario: datosActualizados.comentario,
        nombreEmpleado: datosActualizados.nombre_empleado,
        imagenes: datosActualizados.imagenes || [],
        enviarEmail: datosActualizados.enviarEmail,
      };

      const respComentario = await IncidenciasService.createComment(
        comentarioData
      );

      // 3. Actualizar estado local (estado y comentarios).
      // El "tramitado por" al editar es solo el autor del nuevo comentario;
      // NO modifica el creador de la incidencia (nombreEmpleado/id_empleado se mantienen).
      const incidenciaActualizada = {
        ...incidenciaSeleccionada,
        estado: datosActualizados.estado,
        estadoTexto:
          datosActualizados.estado === ESTADOS.ABIERTA
            ? "Abierto"
            : datosActualizados.estado === ESTADOS.EN_REVISION
            ? "En Revisión"
            : "Cerrado",
        comentarios: [respComentario.data, ...incidenciaSeleccionada.comentarios],
      };

      // 4. Actualizar lista de incidencias
      const incidenciasActualizadas = incidencias
        .map((inc) =>
          inc.id === incidenciaActualizada.id ? incidenciaActualizada : inc
        )
        .filter((inc) => {
          // En vista de solo abiertas, quitar las cerradas
          if (soloAbiertas && Number(inc.estado) === ESTADOS.CERRADA) {
            return false;
          }
          return true;
        });

      setIncidencias(incidenciasActualizadas);

      // 5. Refrescar contador del header
      refrescarContadorIncidencias?.();

      // 6. Mostrar toast de éxito (y aviso si se pidió el correo y no salió)
      const toasts = [
        Toasts.generateToast("Incidencia actualizada correctamente", "success"),
      ];
      if (comentarioData.enviarEmail && !respComentario.emailEnviado) {
        toasts.push(
          Toasts.generateToast(
            "No se pudo enviar el correo: el cliente no tiene email o falló el envío",
            "warning"
          )
        );
      }
      updateState({ toasts: [...state.toasts, ...toasts] });

      // 7. Cerrar modal
      handleCerrarModal();
    } catch (error) {
      const toast = Toasts.generateToast(
        error.message || "Error al actualizar incidencia",
        "error"
      );
      updateState({ toasts: [...state.toasts, toast] });
    } finally {
      setLoadingModal(false);
    }
  };

  return {
    incidencias,
    loading,
    soloAbiertas,
    showModal,
    incidenciaSeleccionada,
    loadingModal,
    toggleFiltro,
    handleEditarIncidencia,
    handleCerrarModal,
    handleConfirmarCambios,
  };
};
